import Cookies from 'js-cookie';

const MEMORY_KEY = 'lm_autotest_v1';
const TRAIL_KEY = 'lm_autotest_trail';
const DOCK_KEY = 'lm_autotest_dock';
const DISMISSAL_CAP = 10;
const TRAIL_CAP = 10;
const RETURN_TO_PAPER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
// Without a floor, the first open of a work arms return_to_paper for every
// later same-work open (each open re-stamps lastOpen), making the dismissal
// rungs unreachable exactly where their copy targets. A reopen only counts
// as a "return" after a real absence.
const RETURN_TO_PAPER_MIN_GAP_MS = 30 * 60 * 1000;

export type MemoryRung =
  | 'first_visit'
  | 'return_to_paper'
  | 'dismissed_once'
  | 'dismissed_repeatedly'
  | 'err';

type OpenRecord = { ts: number; workId?: number };

type StoredMemory = {
  v: 1;
  auvid: string;
  dismissals: OpenRecord[];
  lastOpen?: OpenRecord;
  claimed?: boolean;
};

export type TrailEntry = {
  workId: number;
  title?: string;
  thumbnailUrl?: string;
  ts: number;
};

export type DockState = {
  arm: string;
  detail: Record<string, unknown>;
  ts: number;
};

// window.$auvid stays null until the async record-hit chain runs; the cookie
// it mirrors is available earlier, so it is the fallback rather than a
// separate identity.
function currentAuvid(): string | null {
  return window.$auvid || Cookies.get('auvid') || null;
}

function readStored(auvid: string): StoredMemory | null {
  const raw = localStorage.getItem(MEMORY_KEY);
  if (!raw) return null;

  let parsed: StoredMemory;
  try {
    parsed = JSON.parse(raw) as StoredMemory;
  } catch {
    localStorage.removeItem(MEMORY_KEY);
    return null;
  }

  if (
    parsed?.v !== 1 ||
    parsed.auvid !== auvid ||
    !Array.isArray(parsed.dismissals)
  ) {
    localStorage.removeItem(MEMORY_KEY);
    return null;
  }
  return parsed;
}

// Read the rung BEFORE recordOpen for the same open: lastOpen must still be
// the previous visit for return-to-paper to mean a return.
export function memoryRung(workId?: number): MemoryRung {
  try {
    const auvid = currentAuvid();
    if (!auvid) return 'err';

    const stored = readStored(auvid);
    if (!stored) return 'first_visit';

    const { lastOpen } = stored;
    if (
      workId != null &&
      lastOpen?.workId === workId &&
      Date.now() - lastOpen.ts <= RETURN_TO_PAPER_WINDOW_MS &&
      Date.now() - lastOpen.ts >= RETURN_TO_PAPER_MIN_GAP_MS
    ) {
      return 'return_to_paper';
    }
    if (stored.dismissals.length >= 2) return 'dismissed_repeatedly';
    if (stored.dismissals.length === 1) return 'dismissed_once';
    return 'first_visit';
  } catch {
    return 'err';
  }
}

export function hasClaimed(): boolean {
  try {
    const auvid = currentAuvid();
    return auvid != null && readStored(auvid)?.claimed === true;
  } catch {
    return false;
  }
}

function writeStored(mutate: (stored: StoredMemory) => void): void {
  try {
    const auvid = currentAuvid();
    if (!auvid) return;

    const stored = readStored(auvid) ?? {
      v: 1 as const,
      auvid,
      dismissals: [],
    };
    mutate(stored);
    localStorage.setItem(MEMORY_KEY, JSON.stringify(stored));
  } catch {
    /* storage unavailable — degrades to first-visit behavior */
  }
}

export function recordOpen(workId?: number): void {
  writeStored((stored) => {
    stored.lastOpen = { ts: Date.now(), ...(workId != null && { workId }) };
  });
}

export function recordDismissal(workId?: number): void {
  writeStored((stored) => {
    stored.dismissals = [
      ...stored.dismissals,
      { ts: Date.now(), ...(workId != null && { workId }) },
    ].slice(-DISMISSAL_CAP);
  });
}

export function recordClaimed(): void {
  writeStored((stored) => {
    stored.claimed = true;
  });
}

function isTrailEntry(entry: unknown): entry is TrailEntry {
  const candidate = entry as { workId?: unknown; ts?: unknown } | null;
  return (
    typeof candidate?.workId === 'number' && typeof candidate.ts === 'number'
  );
}

export function readTrail(): TrailEntry[] {
  try {
    const raw = sessionStorage.getItem(TRAIL_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isTrailEntry) : [];
  } catch {
    return [];
  }
}

// Deduped by workId: the session-trail predicate counts distinct works.
export function pushTrailEntry(entry: Omit<TrailEntry, 'ts'>): void {
  try {
    const trail = readTrail().filter((seen) => seen.workId !== entry.workId);
    trail.push({ ...entry, ts: Date.now() });
    sessionStorage.setItem(TRAIL_KEY, JSON.stringify(trail.slice(-TRAIL_CAP)));
  } catch {
    /* storage unavailable */
  }
}

export function saveDockState(state: Omit<DockState, 'ts'>): void {
  try {
    sessionStorage.setItem(
      DOCK_KEY,
      JSON.stringify({ ...state, ts: Date.now() }),
    );
  } catch {
    /* storage unavailable */
  }
}

export function readDockState(): DockState | null {
  try {
    const raw = sessionStorage.getItem(DOCK_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as DockState;
    if (
      typeof parsed?.arm !== 'string' ||
      typeof parsed.ts !== 'number' ||
      parsed.detail == null ||
      typeof parsed.detail !== 'object'
    ) {
      sessionStorage.removeItem(DOCK_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearDockState(): void {
  try {
    sessionStorage.removeItem(DOCK_KEY);
  } catch {
    /* storage unavailable */
  }
}
